import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, throwError, from, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { LoginService } from '@uoa/auth';
import { BypassErrorService, UoaErrorsConfig } from '@uoa/error-pages';
import { environment } from 'src/environments/environment';
import { CatchitEnvironment } from '../interfaces/environment.interface';
import { NetworkDetector } from './network-detector.service';
import { ExternalLoginService } from './external-login.service';

export interface CatchitHttpOptions {
  headers?: HttpHeaders | { [header: string]: string | string[] };
  params?: HttpParams | { [param: string]: string | string[] };
  reportProgress?: boolean;
  withCredentials?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class CatchitHttpService {
  private env: CatchitEnvironment;

  constructor(
    private http: HttpClient,
    private loginService: LoginService,
    private bypassErrorService: BypassErrorService,
    private errorPagesConfig: UoaErrorsConfig,
    private networkDetector: NetworkDetector,
    private externalLoginService: ExternalLoginService
  ) {
    this.env = environment as CatchitEnvironment;
  }

  /**
   * GET request with automatic bearer token
   */
  get<T>(endpoint: string, options?: CatchitHttpOptions): Observable<T> {
    return this.makeRequest<T>('GET', endpoint, null, options);
  }

  /**
   * POST request with automatic bearer token
   */
  post<T>(endpoint: string, body?: any, options?: CatchitHttpOptions): Observable<T> {
    return this.makeRequest<T>('POST', endpoint, body, options);
  }

  /**
   * PUT request with automatic bearer token
   */
  put<T>(endpoint: string, body?: any, options?: CatchitHttpOptions): Observable<T> {
    return this.makeRequest<T>('PUT', endpoint, body, options);
  }

  /**
   * DELETE request with automatic bearer token
   */
  delete<T>(endpoint: string, options?: CatchitHttpOptions): Observable<T> {
    return this.makeRequest<T>('DELETE', endpoint, null, options);
  }

  /**
   * PATCH request with automatic bearer token
   */
  patch<T>(endpoint: string, body?: any, options?: CatchitHttpOptions): Observable<T> {
    return this.makeRequest<T>('PATCH', endpoint, body, options);
  }

  /**
   * Make HTTP request with bearer token authentication
   */
  private makeRequest<T>(
    method: string,
    endpoint: string,
    body?: any,
    options?: CatchitHttpOptions
  ): Observable<T> {
    return from(this.getAuthenticatedHeaders(options?.headers, this.isPublicEndpoint(endpoint))).pipe(
      switchMap(headers => {
        const url = this.buildUrl(endpoint);

        // @uoa/error-pages registers its own clientErrorInterceptor/serverErrorInterceptor
        // (see main.ts's provideErrorPages()) that independently hard-navigates the whole
        // app to a dedicated /error/{code} page for any 4xx/5xx response - entirely unaware
        // of whether we already know we're offline. Confirmed live: being offline caused a
        // genuine 504 (not just an instant connection failure), which that interceptor then
        // used to navigate away from wherever the user was, on top of CatchitErrorHandlerService's
        // own alert for the same event - stacking two independent, uncoordinated error
        // systems' reactions onto one offline moment, when the warning triangle in the
        // header already communicates it. bypassError() is @uoa/error-pages' own supported
        // extension point for "don't react to this URL's errors" - registering it here,
        // right before dispatch, rather than fighting over interceptor ordering between two
        // separate provideHttpClient() registrations (main.ts's own vs @uoa/error-pages')
        // that can't be verified without a working local build.
        if (this.networkDetector.getStatus()?.connected === false) {
          this.bypassErrorService.bypassError(url,
              [...this.errorPagesConfig.clientErrorCodes, ...this.errorPagesConfig.serverErrorCodes]);
        }

        // getAuthenticatedHeaders() unconditionally sets Content-Type: application/json -
        // fine for JSON bodies, but for FormData (file uploads) it overrides the boundary
        // the browser would otherwise auto-generate, leaving the backend unable to parse
        // the request as multipart at all. Confirmed live: this caused Spring's "Current
        // request is not a multipart request" error on every single file upload.
        if (body instanceof FormData) {
          headers = headers.delete('Content-Type');
        }

        const httpOptions = {
          ...options,
          headers
        };

        switch (method.toUpperCase()) {
          case 'GET':
            return this.http.get<T>(url, httpOptions);
          case 'POST':
            return this.http.post<T>(url, body, httpOptions);
          case 'PUT':
            return this.http.put<T>(url, body, httpOptions);
          case 'DELETE':
            return this.http.delete<T>(url, httpOptions);
          case 'PATCH':
            return this.http.patch<T>(url, body, httpOptions);
          default:
            return throwError(() => new Error(`Unsupported HTTP method: ${method}`));
        }
      }),
      catchError(error => {
        // ErrorInterceptor (HTTP_INTERCEPTORS) already calls CatchitErrorHandlerService
        // for every failed request and shows the user-facing alert - calling it again here
        // showed a second, identical alert stacked on top of the first for every single
        // HTTP failure app-wide. This still converts the error into a null success value,
        // matching every caller's existing null-check-on-failure pattern; it just no longer
        // duplicates the alert.
        console.error(`HTTP ${method} error for ${endpoint}:`, error);
        return of(null as T);
      })
    );
  }

  /**
   * Matches the backend's own /public/** convention (see PublicUsersController) - these
   * endpoints don't require auth, so the frontend shouldn't demand a logged-in session
   * to call them either. Without this, anyone not already logged in (the entire audience
   * for sign-up, forgotten-password, etc.) gets a client-side "User not authenticated"
   * error before the request is even sent.
   */
  private isPublicEndpoint(endpoint: string): boolean {
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint : '/' + endpoint;
    return cleanEndpoint.startsWith('/public/') || cleanEndpoint.startsWith('/.well-known/');
  }

  /**
   * Get authenticated headers with bearer token
   */
  private async getAuthenticatedHeaders(
    existingHeaders?: HttpHeaders | { [header: string]: string | string[] },
    isPublicEndpoint = false
  ): Promise<HttpHeaders> {
    let headers: HttpHeaders;

    if (existingHeaders instanceof HttpHeaders) {
      headers = existingHeaders;
    } else {
      headers = new HttpHeaders(existingHeaders);
    }

    // For local development: use mock authentication
    if (!this.env.production && this.env.apiBaseUrl?.includes('localhost')) {
      console.log('CatchitHttpService: Using mock authentication for local development');

      // Create a simple mock JWT payload
      const mockJwtPayload = {
        sub: 'mock-user-123',
        email: 'mock@test.com',
        given_name: 'Test',
        family_name: 'User',
        'cognito:groups': ['catchit-user', 'catchit-admin', 'catchit-super-admin'],
        'custom:catchit_area_id': '1',
        'custom:catchit_line_id': '1',
        'custom:catchit_paid': 'true',
        'custom:catchit_welcome': 'false',
        iat: Math.floor(Date.now() / 1000),
        exp: Math.floor(Date.now() / 1000) + (60 * 60) // 1 hour expiry
      };

      // Create a mock JWT (just base64 encoded, not properly signed)
      const mockJwt = 'eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.' +
                      btoa(JSON.stringify(mockJwtPayload)) +
                      '.mock-signature-for-local-dev';

      headers = headers
        .set('Content-Type', 'application/json')
        .set('Authorization', `Bearer ${mockJwt}`);

      return headers;
    }

    if (isPublicEndpoint) {
      return headers.set('Content-Type', 'application/json');
    }

    // Production/remote: use real authentication
    try {
      // isAuthenticated() is false as soon as the 60-minute access token expires, which
      // made every call through this service throw before @uoa/auth's interceptor could
      // refresh it - e.g. saving on a page left open past the hour, or any call offline.
      // The session stands unless Cognito actually rejects the refresh token (same rule as
      // RequireAuthGuard/CognitoUserService); offline, the request then fails as a normal
      // offline request instead.
      const isAuthenticated = await this.loginService.isAuthenticated()
          || await this.externalLoginService.refreshSession() !== 'rejected';
      if (isAuthenticated) {
        // The UoA auth interceptor should automatically add the bearer token
        headers = headers.set('Content-Type', 'application/json');
      } else {
        throw new Error('User not authenticated');
      }
    } catch (error) {
      console.error('Error getting authentication headers:', error);
      throw error;
    }

    return headers;
  }

  /**
   * Build full URL from endpoint
   */
  private buildUrl(endpoint: string): string {
    const baseUrl = this.env.apiBaseUrl || 'http://localhost:8090/catchit/api';

    // Remove leading slash from endpoint if present
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;

    // Ensure base URL doesn't end with slash
    const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;

    return `${cleanBaseUrl}/${cleanEndpoint}`;
  }

  /**
   * Check if user is authenticated
   */
  async isAuthenticated(): Promise<boolean> {
    try {
      return await this.loginService.isAuthenticated();
    } catch {
      return false;
    }
  }

  /**
   * Get current user info
   */
  async getCurrentUser() {
    try {
      return await this.loginService.getUserInfo();
    } catch (error) {
      console.error('Error getting current user:', error);
      throw error;
    }
  }
}